import React, { useState } from 'react';
import styles from './styles.module.scss';
import whatsappIcon from '../../assets/icons/whatsapp.svg';

interface GuideProps {
	onNavigateTab: (tab: 'prizes' | 'settings' | 'media' | 'guide') => void;
}

interface FaqItem {
	question: string;
	answer: string;
}

const FAQS: FaqItem[] = [
	{
		question: 'De ce nu mai selectez un eveniment la crearea contului?',
		answer:
			'Înregistrarea este acum simplificată și flexibilă! Te poți înscrie direct fără a fi limitat la un singur eveniment. Oricând dorești, poți adăuga unul sau mai multe evenimente din tab-ul Setări -> Evenimentele Mele.',
	},
	{
		question: 'Cum știu dacă cineva a folosit codul meu promoțional?',
		answer:
			'Numărul de bilete vândute și punctele acumulate se actualizează automat în timp real. Le poți vedea oricând în antetul profilului tău (Bilete Vândute & Puncte Rămase).',
	},
	{
		question: 'Pot promova evenimente din mai multe orașe simultan?',
		answer:
			'Da! Poți adăuga oricâte evenimente dorești în secțiunea Setări. Codul tău este valabil pentru evenimentele active Maneliada, iar vânzările se cumulează pe același cont.',
	},
	{
		question: 'Cum funcționează validarea premiilor la eveniment?',
		answer:
			'După ce revendici un premiu din tab-ul Premii, acesta va apărea în lista ta de premii revendicate cu statusul „În așteptare”. La intrarea în club sau la bar, arată ecranul cu premiul revendicat echipei Maneliada pentru validare.',
	},
	{
		question: 'Cum primesc invitația la eveniment?',
		answer:
			'Când revendici un premiu de tip Invitație, dacă ai mai multe evenimente în profil, vei alege evenimentul dorit. Invitația ta este înregistrată automat pe lista de acces a evenimentului.',
	},
	{
		question: 'Unde găsesc materiale grafice și video pentru promovare?',
		answer:
			'În tab-ul Media găsești afișe, story-uri și clipuri video oficiale Maneliada, gata optimizate pentru Instagram Stories, TikTok și alte rețele.',
	},
];

const Guide: React.FC<GuideProps> = ({ onNavigateTab }) => {
	const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

	const toggleFaq = (index: number) => {
		setOpenFaqIndex(prev => (prev === index ? null : index));
	};

	return (
		<div className={styles.guideContainer}>
			{/* Hero / Intro Card */}
			<div className={styles.guideHero}>
				<h2 className={styles.guideHeroTitle}>Ghidul Ambasadorului Maneliada</h2>
				<p className={styles.guideHeroSubtitle}>
					Tot ce trebuie să știi despre cum funcționează programul, acumularea de puncte,
					alegerea evenimentelor și revendicarea premiilor.
				</p>

				<div className={styles.guidePillsGrid}>
					<div className={styles.guidePillCard}>
						<span className={styles.guidePillIcon}>🎟️</span>
						<div className={styles.guidePillText}>
							<strong>Reducere la Bilete</strong>
							<span>Codul tău oferă discount direct cumpărătorilor</span>
						</div>
					</div>
					<div className={styles.guidePillCard}>
						<span className={styles.guidePillIcon}>⚡</span>
						<div className={styles.guidePillText}>
							<strong>4 Bilete = 1 Punct</strong>
							<span>Punctele se calculează și se adaugă automat</span>
						</div>
					</div>
					<div className={styles.guidePillCard}>
						<span className={styles.guidePillIcon}>🎁</span>
						<div className={styles.guidePillText}>
							<strong>Premii Exclusive</strong>
							<span>Invitații VIP, shot-uri, bar și merchandise</span>
						</div>
					</div>
				</div>
			</div>

			{/* Step-by-Step Cards */}
			<div className={styles.guideStepsSection}>
				<h3 className={styles.guideSectionTitle}>Cum Funcționează Programul</h3>

				<div className={styles.guideStepsGrid}>
					{/* Step 1: Select Events */}
					<div className={styles.guideStepCard}>
						<div className={styles.guideStepHeader}>
							<div className={styles.guideStepNumber}>1</div>
							<div>
								<h4 className={styles.guideStepTitle}>Selectează Evenimentele</h4>
								<span className={styles.guideStepBadge}>Personalizare</span>
							</div>
						</div>
						<div className={styles.guideStepBody}>
							<p>
								La înregistrare nu mai este obligatoriu să alegi un eveniment. Poți promova
								orice eveniment din țară!
							</p>
							<p>
								Pentru a-ți asocia profilul cu un eveniment și a putea primi invitații specifice:
							</p>
							<ol>
								<li>Mergi la tab-ul <strong>Setări</strong>.</li>
								<li>În secțiunea <strong>„Evenimentele Mele”</strong>, alege evenimentul dorit din listă.</li>
								<li>Apasă pe <strong>„Adaugă Eveniment”</strong>.</li>
							</ol>
						</div>
						<button
							className={styles.guideStepBtn}
							onClick={() => onNavigateTab('settings')}
						>
							Mergi la Setări →
						</button>
					</div>

					{/* Step 2: Share Promo Code & Media */}
					<div className={styles.guideStepCard}>
						<div className={styles.guideStepHeader}>
							<div className={styles.guideStepNumber}>2</div>
							<div>
								<h4 className={styles.guideStepTitle}>Distribuie Codul & Media</h4>
								<span className={styles.guideStepBadge}>Promovare</span>
							</div>
						</div>
						<div className={styles.guideStepBody}>
							<p>
								Codul tău promoțional unic este afișat în bara de sus a dashboard-ului.
							</p>
							<p>
								Trimite-l prietenilor tăi sau promovează-l pe rețelele sociale (Instagram Stories,
								TikTok, grupuri de WhatsApp).
							</p>
							<p>
								În tab-ul <strong>Media</strong> găsești afișe, postere și clipuri video oficiale
								Maneliada pe care le poți descărca instant.
							</p>
						</div>
						<button
							className={styles.guideStepBtn}
							onClick={() => onNavigateTab('media')}
						>
							Descarcă Materiale Media →
						</button>
					</div>

					{/* Step 3: Earn Points */}
					<div className={styles.guideStepCard}>
						<div className={styles.guideStepHeader}>
							<div className={styles.guideStepNumber}>3</div>
							<div>
								<h4 className={styles.guideStepTitle}>Acumulează Puncte</h4>
								<span className={styles.guideStepBadge}>Conversie Automată</span>
							</div>
						</div>
						<div className={styles.guideStepBody}>
							<p>
								La fiecare <strong>4 bilete cumpărate</strong> folosind codul tău de reducere,
								primești automat <strong>1 punct</strong> în cont.
							</p>
							<p>
								Statisticile se actualizează automat: poți urmări în timp real numărul de bilete
								vândute și punctele disponibile în antetul profilului tău.
							</p>
						</div>
					</div>

					{/* Step 4: Redeem Prizes */}
					<div className={styles.guideStepCard}>
						<div className={styles.guideStepHeader}>
							<div className={styles.guideStepNumber}>4</div>
							<div>
								<h4 className={styles.guideStepTitle}>Revendică Premiile</h4>
								<span className={styles.guideStepBadge}>Recompense</span>
							</div>
						</div>
						<div className={styles.guideStepBody}>
							<p>
								Când ai acumulat puncte suficiente, accesează tab-ul <strong>Premii</strong>:
							</p>
							<ul>
								<li>
									<strong>Invitații la Eveniment:</strong> Dacă ai mai multe evenimente adăugate,
									vei selecta evenimentul pentru care dorești biletul/invitația.
								</li>
								<li>
									<strong>Bar & Merchandise:</strong> Revendică premiul și prezintă panoul de
									ambasador la bar sau la organizatori pentru validare la fața locului.
								</li>
							</ul>
						</div>
						<button
							className={styles.guideStepBtn}
							onClick={() => onNavigateTab('prizes')}
						>
							Vezi Premiile Disponibile →
						</button>
					</div>
				</div>
			</div>

			{/* FAQ Accordion Section */}
			<div className={styles.card} style={{ marginTop: '2rem' }}>
				<h3 className={styles.cardTitle}>Întrebări Frecvente (FAQ)</h3>
				<div className={styles.faqList}>
					{FAQS.map((faq, idx) => {
						const isOpen = openFaqIndex === idx;
						return (
							<div
								key={idx}
								className={`${styles.faqItem} ${isOpen ? styles.faqItemOpen : ''}`}
							>
								<button
									className={styles.faqQuestionBtn}
									onClick={() => toggleFaq(idx)}
									type="button"
									aria-expanded={isOpen}
								>
									<span className={styles.faqQuestionText}>{faq.question}</span>
									<span className={styles.faqIcon}>{isOpen ? '−' : '+'}</span>
								</button>
								{isOpen && (
									<div className={styles.faqAnswer}>
										<p>{faq.answer}</p>
									</div>
								)}
							</div>
						);
					})}
				</div>
			</div>

			{/* Support & WhatsApp Card */}
			<div className={styles.guideSupportCard}>
				<div className={styles.guideSupportInfo}>
					<div className={styles.guideSupportIconWrap}>
						<img src={whatsappIcon} alt="WhatsApp" />
					</div>
					<div>
						<h4 className={styles.guideSupportTitle}>Comunitatea Ambasadorilor pe WhatsApp</h4>
						<p className={styles.guideSupportDesc}>
							Alătură-te grupului oficial de WhatsApp pentru întrebări, noutăți despre line-up,
							concursuri exclusive și suport direct din partea echipei Maneliada.
						</p>
					</div>
				</div>
				<a
					href="https://chat.whatsapp.com/Ht4M3ATTveGGsipgUJCaIm"
					target="_blank"
					rel="noopener noreferrer"
					className={styles.guideSupportBtn}
				>
					<img src={whatsappIcon} alt="" />
					<span>Intră pe Grup</span>
				</a>
			</div>
		</div>
	);
};

export default Guide;
